import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { usePost } from '../API';
import { Account } from '@aidkitorg/aidkit/lib/admin_panel/accounts';
import { AccountBalanceReminder, AccountBalanceReminderConfig, EditAccountBalanceReminderParams, PaymentReminder, EditPaymentReminderParams, PaymentFailureReminderConfig, PaymentStatusSummaryReminderConfig } from '@aidkitorg/aidkit/lib/admin_panel/reminders';
import { logAdminPanelError } from '../utils/adminPanel.utils';

type AccountsContextType = {
  totalBalance: number | undefined,
  accounts: Array<Account>
  subledgers: string[]
  accountsDataTimestamp?: number

  accountBalanceReminders: Array<AccountBalanceReminder>,
  paymentReminders: Array<PaymentReminder>,

  isAccountsLoading: boolean,
  reminderCallInFlight: boolean,

  fetchAccountsOverview: (force?: boolean) => void,
  fetchAccountBalanceReminders: () => void,
  fetchPaymentReminders: () => void,
  createAccountBalanceReminder: ( params: AccountBalanceReminderConfig ) => Promise<boolean>,
  editAccountBalanceReminder: ( params: EditAccountBalanceReminderParams ) => void,
  createPaymentFailureReminder: ( params: PaymentFailureReminderConfig ) => Promise<boolean>,
  createPaymentStatusSummaryReminder: ( params: PaymentStatusSummaryReminderConfig ) => Promise<boolean>,
  editPaymentReminder: ( params: EditPaymentReminderParams ) => void
}

type AccountsOverviewRef = {
  accounts?: Array<Account>,
  timestamp?: number,
  expiresAt?: number,
}

type AccountsProviderProps = {
  children: React.ReactNode
}

const AccountsContext = createContext<AccountsContextType>({
  totalBalance: undefined,
  accounts: [],
  subledgers: [],

  accountBalanceReminders: [],
  paymentReminders: [],

  isAccountsLoading: false,
  reminderCallInFlight: false,

  fetchAccountsOverview: async (_force?: boolean) => {},
  fetchAccountBalanceReminders: async () => {},
  fetchPaymentReminders: async () => {},
  createAccountBalanceReminder: async (_params?: AccountBalanceReminderConfig) => { return true },
  editAccountBalanceReminder: async (_params?: EditAccountBalanceReminderParams) => {},
  editPaymentReminder: async (_params?: EditPaymentReminderParams) => {},
  createPaymentFailureReminder: async (_params?: PaymentFailureReminderConfig) => { return true },
  createPaymentStatusSummaryReminder: async (_params?: PaymentStatusSummaryReminderConfig) => { return true },
})

export const useAccounts = () => {
  const context = useContext(AccountsContext)
  if (!context) {
    throw new Error("useAccounts must be used within an AccountsProvider")
  }

  return context;
}

export const AccountsProvider: React.FC<AccountsProviderProps> = ({ children }) => {
  const [totalBalance, setTotalBalance] = useState<number | undefined>()
  const [accounts, setAccounts] = useState<Array<Account>>([])
  const [subledgers, setSubledgers] = useState<string[]>([])
  const [accountsDataTimestamp, setAccountsDataTimestamp] = useState<number>()

  const [accountBalanceReminders, setAccountBalanceReminders] = useState<Array<AccountBalanceReminder>>([])
  const [paymentReminders, setPaymentReminders] = useState<Array<PaymentReminder>>([])

  const [isAccountsLoading, setIsAccountsLoading] = useState(false)
  const [reminderCallInFlight, setReminderCallInFlight] = useState(false)

  const accountsRef = useRef<AccountsOverviewRef>({})

  const getAccountsData = usePost("/api/admin_panel/accounts")

  const getAccountBalanceReminders = usePost("/api/admin_panel/accounts/balance_reminders")
  const createAccountBalanceReminderPost = usePost("/api/admin_panel/accounts/balance_reminders/create")
  const editAccountBalanceReminderPost = usePost("/api/admin_panel/accounts/balance_reminders/edit")

  const getPaymentReminders = usePost("/api/admin_panel/payments/payment_reminders")
  const createPaymentReminder = usePost("/api/admin_panel/payments/payment_reminders/create")
  const editPaymentReminderPost = usePost("/api/admin_panel/payments/payment_reminders/edit")

  const fetchAccountsOverview = useCallback(async (force?: boolean) => {
    const now = Date.now()

    if (!force &&
      accountsRef.current.accounts && 
      (accountsRef.current.expiresAt && now < accountsRef.current.expiresAt)
    ) {
      return
    }

    setIsAccountsLoading(true)
    const result = await getAccountsData({ force })
    setIsAccountsLoading(false)

    if (result && result.success && result.data) {
      accountsRef.current = {
        accounts: result.data.accounts,
        timestamp: result.data.timestamp,
        expiresAt: result.data.expiresAt
      }

      setTotalBalance(result.data.totalBalance)
      setAccounts(result.data.accounts)
      setSubledgers(result.data.subledgers || [])
      setAccountsDataTimestamp(result.data.timestamp)
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }, [getAccountsData])

  const fetchAccountBalanceReminders = useCallback(async () => {
    setReminderCallInFlight(true)
    const result = await getAccountBalanceReminders({})
    setReminderCallInFlight(false)
    
    if (result && result.success && result.data) {
      setAccountBalanceReminders(result.data.reminders)
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }, [ getAccountBalanceReminders ])

  const createAccountBalanceReminder = useCallback(async ( params: AccountBalanceReminderConfig ) => {
    setReminderCallInFlight(true)
    const result = await createAccountBalanceReminderPost(params)

    if (result && !result.success && result.error) {
      logAdminPanelError(result.error, result.message)
    }
    await fetchAccountBalanceReminders()
    
    setReminderCallInFlight(false)

    return result.success
  }, [ createAccountBalanceReminderPost, fetchAccountBalanceReminders ])

  const editAccountBalanceReminder = useCallback(async (params: EditAccountBalanceReminderParams) => {
    setReminderCallInFlight(true)

    const result = await editAccountBalanceReminderPost(params)

    if (result && !result.success && result.error) {
      logAdminPanelError(result.error, result.message)
    } else {
      await fetchAccountBalanceReminders()
    }

    setReminderCallInFlight(false)
  }, [ editAccountBalanceReminderPost])

  const fetchPaymentReminders = useCallback(async () => {
    setReminderCallInFlight(true)
    const result = await getPaymentReminders({})
    setReminderCallInFlight(false)
    
    if (result && result.success && result.data) {
      setPaymentReminders(result.data.reminders)
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }, [getPaymentReminders])

  const editPaymentReminder = useCallback(async (params: EditPaymentReminderParams) => {
    setReminderCallInFlight(true)

    const result = await editPaymentReminderPost(params)

    if (result && !result.success && result.error) {
      logAdminPanelError(result.error, result.message)
    } else {
      await fetchPaymentReminders()
    }

    setReminderCallInFlight(false)
  }, [editPaymentReminderPost, fetchPaymentReminders])

  const createPaymentFailureReminder = useCallback(async (params: PaymentFailureReminderConfig) => {
    setReminderCallInFlight(true)
    const result = await createPaymentReminder({
      type: 'payment_failure',
      frequency: params.frequency,
      emails: params.emails
    })

    if (result && !result.success && result.error) {
      logAdminPanelError(result.error, result.message)
    }

    await getPaymentReminders({})
    
    setReminderCallInFlight(false)

    return result?.success ?? false
  }, [createPaymentReminder, getPaymentReminders])

  const createPaymentStatusSummaryReminder = useCallback(async (params: PaymentStatusSummaryReminderConfig) => {
    setReminderCallInFlight(true)
    const result = await createPaymentReminder({
      type: 'payment_status_summary',
      frequency: params.frequency,
      emails: params.emails
    })

    if (result && !result.success && result.error) {
      logAdminPanelError(result.error, result.message)
    }

    await getPaymentReminders({})

    setReminderCallInFlight(false)
    return result?.success ?? false
  }, [createPaymentReminder, getPaymentReminders])

  const value = {
    totalBalance: totalBalance,
    accounts: accounts,
    subledgers: subledgers,
    accountsDataTimestamp: accountsDataTimestamp,

    accountBalanceReminders: accountBalanceReminders,
    paymentReminders: paymentReminders,

    isAccountsLoading: isAccountsLoading,
    reminderCallInFlight: reminderCallInFlight,

    fetchAccountsOverview: fetchAccountsOverview,
    fetchAccountBalanceReminders: fetchAccountBalanceReminders,
    fetchPaymentReminders: fetchPaymentReminders,
    createAccountBalanceReminder: createAccountBalanceReminder,
    editAccountBalanceReminder: editAccountBalanceReminder,
    createPaymentFailureReminder: createPaymentFailureReminder,
    editPaymentReminder: editPaymentReminder,
    createPaymentStatusSummaryReminder: createPaymentStatusSummaryReminder,
  }

  return (
    <AccountsContext.Provider value={value}>
      {children}
    </AccountsContext.Provider>
  )
}